import type { LimitService } from '.';
import type {
  PartnerSearchCostAdjustParams,
  PartnerCostAdjustConfig,
  PartnerCostAdjustConfigCreate,
  PartnerCostAdjustConfigPatch,
  PartnerCostAdjustConfigPut,
  PartnerLimitV2,
} from '@shipium-corp/shipium-ts-types';
import { DYNAMIC_LIMIT_EVALUATION_PERIOD } from '../../../components/limit/LimitUtils';

const mockPartnerLimitV2: PartnerLimitV2 = {
  active: true,
  archive: false,
  valid: true,
  invalidReasons: [],
  auditInfo: {
    createdBy: '8cb8e11d-fbd6-42a7-b369-613fb843179b',
    creationTimeStamp: '2022-09-08T22:56:10.471Z',
    lastUpdatedBy: '8cb8e11d-fbd6-42a7-b369-613fb843179b',
    lastUpdatedTimeStamp: '2022-09-08T22:56:10.471Z',
    version: 1,
  },
  carrier: 'ups',
  carrierServiceMethodIds: [],
  constraints: [
    {
      constraintType: 'maximum',
      dayOfWeek: 'MONDAY',
      thresholdType: 'shipment_count',
      thresholdValue: 5000,
    },
    {
      constraintType: 'minimum',
      dayOfWeek: 'MONDAY',
      labelCostAdjustment: {
        type: 'fixed_value',
        value: 0.7,
      },
      thresholdType: 'shipment_count',
      thresholdValue: 1000,
    },
    {
      constraintType: 'maximum',
      dayOfWeek: 'TUESDAY',
      thresholdType: 'shipment_count',
      thresholdValue: 5000,
    },
    {
      constraintType: 'maximum',
      dayOfWeek: 'WEDNESDAY',
      thresholdType: 'shipment_count',
      thresholdValue: 5000,
    },
    {
      constraintType: 'maximum',
      dayOfWeek: 'FRIDAY',
      thresholdType: 'shipment_count',
      thresholdValue: 5000,
    },
    {
      constraintType: 'minimum',
      dayOfWeek: 'FRIDAY',
      labelCostAdjustment: {
        type: 'percentage',
        value: 0.01,
      },
      thresholdType: 'shipment_count',
      thresholdValue: 1500,
    },
  ],
  effectiveDate: '2022-09-08',
  limitId: 'limit123',
  limitType: 'label',
  limitUrn: 'limit123:1',
  name: 'UPS Limit',
  originId: 'origin321',
  partnerId: 'e0758009-622f-45aa-8ccf-a23d212ds23',
};

const mockedCostingLimit = {
  active: true,
  archive: false,
  constraint: {
    labelCostAdjustment: {},
    upgradeSpendThreshold: 3000,
    type: 'maximum',
  },
  limitId: 'lim42',
  limitUrn: 'lim42:1',
  partnerId: 'e0758009-622f-45aa-8ccf-a23d212ds23',
  startDate: '2021-09-09',
  carrierServiceMethodId: '',
  limitType: 'recurring',
  count: 1200,
};

export const mockLimitService: LimitService = {
  fetchPartnerCostingLimits: async () => {
    return [
      {
        active: true,
        archive: false,
        constraint: {
          labelCostAdjustment: {},
          upgradeSpendThreshold: 3000,
          type: 'maximum',
        },
        limitId: 'lim42',
        limitUrn: 'lim42:1',
        partnerId: 'e0758009-622f-45aa-8ccf-a23d212ds23',
        startDate: '2021-09-09',
        localCutoffTime: '10:30',
        localZoneId: '-07:00',
        carrierServiceMethodId: '',
        limitType: 'recurring',
        count: 1200,
      },
    ];
  },
  fetchV2PartnerCostingLimits: async () => {
    return [mockedCostingLimit];
  },
  createPartnerCostingLimit: async (val) => {
    return mockedCostingLimit;
  },
  updatePartnerCostingLimit: async (val) => {
    return mockedCostingLimit;
  },
  deleteLimit: async (val) => {
    return mockedCostingLimit;
  },
  fetchV2Limits: async (params) => {
    return [mockPartnerLimitV2];
  },
  getPartnerLimitV2: async (limitId: string) => {
    return mockPartnerLimitV2;
  },
  createPartnerLimitV2: async (limit) => {
    return { ...mockPartnerLimitV2, ...limit };
  },
  updatePartnerLimitV2: async (limitId, limit) => {
    return { ...mockPartnerLimitV2, ...limit };
  },
  getPartnerShipmentCountLimitSegmentInfo: async ({ limitId, fromDate, toDate }) => {
    return [
      {
        limitId,
        limitSegmentId: '123',
        constraints: [
          {
            constraintType: 'maximum',
            dayOfWeek: 'WEDNESDAY',
            thresholdType: 'shipment_count',
            thresholdValue: 5000,
          },
          {
            constraintType: 'minimum',
            dayOfWeek: 'WEDNESDAY',
            labelCostAdjustment: {
              type: 'fixed_value',
              value: 0.7,
            },
            thresholdType: 'shipment_count',
            thresholdValue: 1000,
          },
        ],
        shipmentCount: 420,
        limitDate: '2022-11-09',
        startDateTime: '2022-11-14T22:30:13.109Z',
        endDateTime: '2022-11-16T22:30:13.109Z',
        timezoneId: 'America/Los_Angeles',
        scheduleEntryId: 'string123',
        carrierScheduleId: null,
        partnerId: 'pid123',
        shipiumTenantId: 'tenantId 123',
        partnerTenantId: 'partner tenny',
        originId: 'fc2zsefes',
        carrier: 'ups',
        scheduleId: 'sid123',
        carrierServiceMethodIds: ['ups-ground'],
      },
      {
        limitId,
        limitSegmentId: '1234',
        constraints: [
          {
            constraintType: 'maximum',
            dayOfWeek: 'THURSDAY',
            thresholdType: 'shipment_count',
            thresholdValue: 5000,
          },
          {
            constraintType: 'minimum',
            dayOfWeek: 'THURSDAY',
            labelCostAdjustment: {
              type: 'fixed_value',
              value: 0.7,
            },
            thresholdType: 'shipment_count',
            thresholdValue: 1000,
          },
        ],
        timezoneId: 'America/Los_Angeles',
        startDateTime: '2022-11-16T22:30:13.109Z',
        endDateTime: '2022-11-17T22:30:13.109Z',
        shipmentCount: 5000,
        limitDate: '2022-11-10',
        scheduleEntryId: 'string123',
        carrierScheduleId: null,
        partnerId: 'pid123',
        shipiumTenantId: null,
        originId: 'fc2zsefes',
        carrier: 'ups',
        scheduleId: 'sid123',
        carrierServiceMethodIds: ['ups-ground'],
      },
    ];
  },
  fetchCostAdjustConfigs: async function (
    searchParams?: PartnerSearchCostAdjustParams | undefined,
  ): Promise<PartnerCostAdjustConfig[]> {
    return [
      {
        active: true,
        archived: false,
        auditInfo: {
          createdBy: '4e72375c-1db4-47c3-a8de-27453414c464',
          creationTimeStamp: '2024-09-09T13:28:19.582Z',
          lastUpdatedBy: '36a6437a-da6a-403d-95e4-c9ccb05e3e1f',
          lastUpdatedTimeStamp: '2024-09-09T13:28:19.582Z',
          version: 3,
        },
        costAdjustConfigId: 'bf7ba330-463a-4511-b190-76c942dbc68f',
        description: 'Keep Above Target example (legacy default behavior)',
        evaluationPeriod: DYNAMIC_LIMIT_EVALUATION_PERIOD,
        excludedCarriers: ['usps', 'upsmi'],
        name: 'Keep UPS above 60%',
        partnerId: '76abb3d4-8990-4f80-aa13-4c2007cb852a',
        partnerProvidedId: 'bf7ba330-463a-4511-b190-76c942dbc68f',
        tagIds: [],
        targetCarrier: 'ups',
        targetPercent: 0.6,
        costAdjustTargetBehavior: 'above',
        seedAdjustment: 0,
        urn: 'bf7ba330-463a-4511-b190-76c942dbc68f:3',
      },
      {
        active: false,
        archived: false,
        auditInfo: {
          createdBy: '4e72375c-1db4-47c3-a8de-27453414c464',
          creationTimeStamp: '2024-09-10T13:28:19.582Z',
          lastUpdatedBy: '36a6437a-da6a-403d-95e4-c9ccb05e3e1f',
          lastUpdatedTimeStamp: '2024-09-10T13:28:19.582Z',
          version: 1,
        },
        costAdjustConfigId: 'aa11bb22-cc33-dd44-ee55-ff6677889900',
        description: 'Keep Below Target example',
        evaluationPeriod: DYNAMIC_LIMIT_EVALUATION_PERIOD,
        excludedCarriers: [],
        name: 'Keep FedEx below 70%',
        partnerId: '76abb3d4-8990-4f80-aa13-4c2007cb852a',
        partnerProvidedId: 'aa11bb22-cc33-dd44-ee55-ff6677889900',
        tagIds: [],
        targetCarrier: 'fedex',
        targetPercent: 0.7,
        costAdjustTargetBehavior: 'below',
        seedAdjustment: 0,
        urn: 'aa11bb22-cc33-dd44-ee55-ff6677889900:1',
      },
      {
        active: false,
        archived: false,
        auditInfo: {
          createdBy: '4e72375c-1db4-47c3-a8de-27453414c464',
          creationTimeStamp: '2024-09-11T13:28:19.582Z',
          lastUpdatedBy: '36a6437a-da6a-403d-95e4-c9ccb05e3e1f',
          lastUpdatedTimeStamp: '2024-09-11T13:28:19.582Z',
          version: 1,
        },
        costAdjustConfigId: '11223344-5566-7788-99aa-bbccddeeff00',
        description: 'Keep At Target example with Fallback Cost Adjustment',
        evaluationPeriod: DYNAMIC_LIMIT_EVALUATION_PERIOD,
        excludedCarriers: [],
        name: 'Keep UPS at 60%',
        partnerId: '76abb3d4-8990-4f80-aa13-4c2007cb852a',
        partnerProvidedId: '11223344-5566-7788-99aa-bbccddeeff00',
        tagIds: [],
        targetCarrier: 'ups',
        targetPercent: 0.6,
        costAdjustTargetBehavior: 'at',
        seedAdjustment: 1.25,
        urn: '11223344-5566-7788-99aa-bbccddeeff00:1',
      },
    ];
  },
  fetchCostAdjustConfig: async function (costAdjustConfigId: string): Promise<PartnerCostAdjustConfig> {
    // Return the matching fixture from fetchCostAdjustConfigs so the detail
    // page can demo all three Limit Behaviors end-to-end.
    const all = await mockLimitService.fetchCostAdjustConfigs();
    const match = all.find((c) => c.costAdjustConfigId === costAdjustConfigId);
    return match ?? all[0];
  },
  createCostAdjustConfig: async function (
    costAdjustConfig: PartnerCostAdjustConfigCreate,
  ): Promise<PartnerCostAdjustConfig> {
    return {
      active: true,
      archived: false,
      auditInfo: {
        createdBy: '4e72375c-1db4-47c3-a8de-27453414c464',
        creationTimeStamp: '2024-09-09T13:28:19.582Z',
        lastUpdatedBy: '041739c4-676c-4658-9a88-b9e9c3b67fe4',
        lastUpdatedTimeStamp: '2024-09-09T13:28:19.582Z',
        version: 2,
      },
      costAdjustConfigId: 'bf7ba330-463a-4511-b190-76c942dbc68f',
      description: 'description_2d600172833d',
      evaluationPeriod: DYNAMIC_LIMIT_EVALUATION_PERIOD,
      excludedCarriers: ['usps', 'upsmi'],
      name: 'Cost config JGC to prepare for Gap',
      partnerId: '76abb3d4-8990-4f80-aa13-4c2007cb852a',
      partnerProvidedId: 'bf7ba330-463a-4511-b190-76c942dbc68f',
      tagIds: [],
      targetCarrier: 'ups',
      targetPercent: 0.81,
      costAdjustTargetBehavior: 'above',
      seedAdjustment: 0,
      urn: 'bf7ba330-463a-4511-b190-76c942dbc68f:2',
    };
  },
  updateCostAdjustConfig: async function (
    costAdjustConfigId: string,
    costAdjustConfig: PartnerCostAdjustConfigPatch,
  ): Promise<PartnerCostAdjustConfig> {
    return {
      active: true,
      archived: false,
      auditInfo: {
        createdBy: '4e72375c-1db4-47c3-a8de-27453414c464',
        creationTimeStamp: '2024-09-09T13:28:19.582Z',
        lastUpdatedBy: '041739c4-676c-4658-9a88-b9e9c3b67fe4',
        lastUpdatedTimeStamp: '2024-09-09T13:28:19.582Z',
        version: 2,
      },
      costAdjustConfigId: 'bf7ba330-463a-4511-b190-76c942dbc68f',
      description: 'description_2d600172833d',
      evaluationPeriod: DYNAMIC_LIMIT_EVALUATION_PERIOD,
      excludedCarriers: ['usps', 'upsmi'],
      name: 'Cost config JGC to prepare for Gap',
      partnerId: '76abb3d4-8990-4f80-aa13-4c2007cb852a',
      partnerProvidedId: 'bf7ba330-463a-4511-b190-76c942dbc68f',
      tagIds: [],
      targetCarrier: 'ups',
      targetPercent: 0.81,
      costAdjustTargetBehavior: 'above',
      seedAdjustment: 0,
      urn: 'bf7ba330-463a-4511-b190-76c942dbc68f:2',
    };
  },
  putCostAdjustConfig: async function (
    costAdjustConfigId: string,
    costAdjustConfig: PartnerCostAdjustConfigPut,
  ): Promise<PartnerCostAdjustConfig> {
    return {
      active: true,
      archived: false,
      auditInfo: {
        createdBy: '4e72375c-1db4-47c3-a8de-27453414c464',
        creationTimeStamp: '2024-09-09T13:28:19.582Z',
        lastUpdatedBy: '041739c4-676c-4658-9a88-b9e9c3b67fe4',
        lastUpdatedTimeStamp: '2024-09-09T13:28:19.582Z',
        version: 2,
      },
      costAdjustConfigId: 'bf7ba330-463a-4511-b190-76c942dbc68f',
      description: 'description_2d600172833d',
      evaluationPeriod: DYNAMIC_LIMIT_EVALUATION_PERIOD,
      excludedCarriers: ['usps', 'upsmi'],
      name: 'Cost config JGC to prepare for Gap',
      partnerId: '76abb3d4-8990-4f80-aa13-4c2007cb852a',
      partnerProvidedId: 'bf7ba330-463a-4511-b190-76c942dbc68f',
      tagIds: [],
      targetCarrier: 'ups',
      targetPercent: 0.81,
      costAdjustTargetBehavior: 'above',
      seedAdjustment: 0,
      urn: 'bf7ba330-463a-4511-b190-76c942dbc68f:2',
    };
  },
};
